import React, { useEffect, useRef, useState } from 'react';
import { Activity, FolderOpen, Bell, Smartphone, Lock, ArrowRight } from 'lucide-react';
import { portalFeatures, portalData } from '../data/mock';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';

const iconMap = {
  Activity: Activity,
  FolderOpen: FolderOpen,
  Bell: Bell,
  Smartphone: Smartphone,
};

const Portal = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const portalCopy = t('portal', portalData);
  const portalFeatureList = t('portal.features', portalFeatures);
  const portalPreview = t('portal.preview', {
    headerTitle: 'Mani projekti',
    headerActive: '3 aktīvi',
    items: [
      { title: 'Cirsmas vērtēšana #127', status: 'Pabeigts', tone: 'success' },
      { title: 'Meža uzmērīšana', status: 'Procesā', tone: 'warning' },
      { title: 'Dokumentācija VMD', status: 'Plānots', tone: 'info' },
    ],
    footerLeft: 'Pēdējā aktivitāte: šodien',
    footerRight: '2 jauni dokumenti'
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="portals" className="py-24 section-portal relative overflow-hidden" data-testid="portal-section" ref={sectionRef}>
      <div className="absolute inset-0 forest-grid" />
      <div className="absolute inset-0 forest-grain opacity-30" />
      <div className="absolute top-16 right-12 w-72 h-72 bg-[#1F3D2B]/8 rounded-full blur-3xl animate-blob" />
      <div className="absolute bottom-12 left-12 w-64 h-64 bg-[#1F3D2B]/6 rounded-full blur-3xl animate-blob" style={{ animationDelay: '2.5s' }} />
      <div className="absolute top-24 left-1/3 w-44 h-2 forest-branch animate-sway" style={{ animationDelay: '0.8s' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`transition-all duration-700 text-center lg:text-left ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.1s' }}>
            <span className="inline-block text-[#1F3D2B] font-medium text-sm tracking-wider uppercase mb-4" data-testid="portal-eyebrow">
              {t('portal.eyebrow', 'Digitālā platforma')}
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#2E2E2E] mb-6" data-testid="portal-title">
              {portalCopy.title}
            </h2>
            <p className="text-[#2E2E2E]/70 text-lg mb-4 leading-relaxed max-w-2xl mx-auto lg:mx-0" data-testid="portal-description">
              {portalCopy.description}
            </p>
            <p className="text-[#2E2E2E]/70 text-base mb-6 leading-relaxed max-w-2xl mx-auto lg:mx-0" data-testid="portal-subtitle">
              {portalCopy.subtitle}
            </p>

            <h3 className="text-sm font-semibold text-[#1F3D2B] uppercase tracking-wide mb-3" data-testid="portal-features-title">{portalCopy.featuresTitle}</h3>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8 justify-items-center lg:justify-items-start" data-testid="portal-features-grid">
              {portalFeatureList.map((feature) => {
                const IconComponent = iconMap[feature.icon];
                return (
                  <div
                    key={feature.id}
                    className="flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 p-4 rounded-xl forest-panel hover:bg-white transition-colors hover-lift" data-testid={`portal-feature-${feature.id}`}
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-5 h-5 text-[#1F3D2B]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#2E2E2E] text-sm mb-1" data-testid={`portal-feature-title-${feature.id}`}>
                        {feature.title}
                      </h3>
                      <p className="text-[#2E2E2E]/60 text-xs" data-testid={`portal-feature-description-${feature.id}`}>
                        {feature.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start">
              <Button
                className="forest-cta shadow-xl hover:shadow-2xl"
                size="lg"
                asChild
              >
                <a href="/portal/login" data-testid="portal-login-button">
                  <Lock className="w-4 h-4 mr-2" />
                  {t('portal.ctaLogin', 'Pieslēgties portālam')}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="forest-cta-outline"
                asChild
              >
                <a href="#kontakti" data-testid="portal-request-access-button">
                  {t('portal.ctaRequest', 'Pieprasīt piekļuvi')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </a>
              </Button>
            </div>
          </div>

          {/* Right - Portal Preview */}
          <div className={`relative transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '0.2s' }}>
            <div className="bg-gradient-to-br from-[#1F3D2B] to-[#2a5039] rounded-2xl p-8 shadow-2xl">
              {/* Mock Portal Interface */}
              <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                {/* Header */}
                <div className="bg-[#f8faf8] px-6 py-4 border-b border-gray-100">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-[#2E2E2E]" data-testid="portal-preview-title">{portalPreview.headerTitle}</span>
                    <span className="text-xs text-[#1F3D2B] bg-[#1F3D2B]/10 px-2 py-1 rounded-full" data-testid="portal-preview-active">{portalPreview.headerActive}</span>
                  </div>
                </div>
                
                {/* Project Items */}
                <div className="p-4 space-y-3">
                  {portalPreview.items.map((item, index) => {
                    const toneClass = item.tone === 'success'
                      ? { bg: 'bg-green-50', border: 'border-green-100', dot: 'bg-green-500', text: 'text-green-700' }
                      : item.tone === 'warning'
                        ? { bg: 'bg-amber-50', border: 'border-amber-100', dot: 'bg-amber-500', text: 'text-amber-700' }
                        : { bg: 'bg-blue-50', border: 'border-blue-100', dot: 'bg-blue-500', text: 'text-blue-700' };

                    return (
                      <div key={`${item.title}-${index}`} className={`flex items-center justify-between p-3 rounded-lg border ${toneClass.bg} ${toneClass.border}`}>
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${toneClass.dot}`} />
                          <span className="text-sm text-[#2E2E2E]" data-testid={`portal-preview-item-${index}`}>{item.title}</span>
                        </div>
                        <span className={`text-xs font-medium ${toneClass.text}`} data-testid={`portal-preview-status-${index}`}>{item.status}</span>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="px-6 py-3 bg-[#f8faf8] border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs text-[#2E2E2E]/60">
                    <span data-testid="portal-preview-footer-left">{portalPreview.footerLeft}</span>
                    <span data-testid="portal-preview-footer-right">{portalPreview.footerRight}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative elements */}
            <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#1F3D2B]/10 rounded-full blur-2xl" />
            <div className="absolute -top-4 -left-4 w-16 h-16 bg-[#1F3D2B]/10 rounded-full blur-xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Portal;
